import { useMemo, useState } from "react";

type Plan = {
  provider: string;
  type: "BNPL" | "Bank EMI" | "Extended";
  tenor: number; // months
  apr: number; // % per annum, 0 for 0% BNPL
  fee: number; // flat fee in AED if any
  note?: string;
};

const SAMPLE_PLANS: Plan[] = [
  { provider: "Tabby",        type: "BNPL",     tenor: 4,  apr: 0,    fee: 0,  note: "Pay in 4" },
  { provider: "Tamara",       type: "BNPL",     tenor: 6,  apr: 0,    fee: 25, note: "Small admin fee" },
  { provider: "Cashew",       type: "Extended", tenor: 9,  apr: 3.5,  fee: 0 },
  { provider: "FAB",          type: "Bank EMI", tenor: 9,  apr: 4.99, fee: 0 },
  { provider: "Emirates NBD", type: "Bank EMI", tenor: 12, apr: 6.99, fee: 0,  note: "Reducing balance" },
  { provider: "Postpay",      type: "Extended", tenor: 12, apr: 4.2,  fee: 0 },
];

function monthlyFromAPR(amount: number, apr: number, months: number) {
  if (apr <= 0) return amount / months;
  const r = apr / 100 / 12;
  return (amount * r) / (1 - Math.pow(1 + r, -months));
}

type Selected = null | {
  plan: Plan;
  monthly: number;
  total: number;
  amount: number;
  category: "Travel" | "Education" | "Healthcare";
};

export default function CompareDemo() {
  const [amount, setAmount] = useState(8000);
  const [category, setCategory] = useState<"Travel" | "Education" | "Healthcare">("Travel");
  const [selected, setSelected] = useState<Selected>(null);

  const rows = useMemo(() => {
    return SAMPLE_PLANS.map(p => {
      const base = monthlyFromAPR(amount, p.apr, p.tenor);
      const feeSpread = p.fee ? p.fee / p.tenor : 0;
      const monthly = base + feeSpread;
      const total = monthly * p.tenor;
      return { ...p, monthly: Math.round(monthly), total: Math.round(total) };
    }).sort((a, b) => a.total - b.total);
  }, [amount]);

  return (
    <section className="py-12">
      <div className="max-w-5xl mx-auto px-4">
        <div className="rounded-2xl border bg-card p-6 shadow-sm">
          {/* Header */}
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h3 className="text-2xl font-semibold text-foreground">Compare plans (demo)</h3>
              <p className="text-sm text-muted-foreground">
                Enter an amount and preview BNPL vs EMI vs extended plans.
              </p>
            </div>
            <div className="flex gap-3">
              <select
                value={category}
                onChange={e => setCategory(e.target.value as any)}
                className="rounded-xl border px-3 py-2 bg-background"
                aria-label="Category"
              >
                <option>Travel</option>
                <option>Education</option>
                <option>Healthcare</option>
              </select>
              <div className="relative">
                <span className="absolute -top-2 -right-2 rounded-md border bg-background px-2 py-0.5 text-[10px] font-semibold">
                  AED
                </span>
                <input
                  type="number"
                  min={500}
                  step={100}
                  value={amount}
                  onChange={e => setAmount(Number(e.target.value))}
                  className="w-40 rounded-xl border px-3 py-2 bg-background"
                  placeholder="Amount"
                  aria-label="Amount in AED"
                />
              </div>
            </div>
          </div>

          {/* Grid */}
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {rows.map(p => (
              <div
                key={p.provider + p.tenor}
                className="relative rounded-2xl border p-4 bg-background shadow-sm hover:shadow transition"
              >
                {/* AED badge top right of the card */}
                <span className="absolute top-3 right-3 rounded-md border bg-card px-2 py-0.5 text-[10px] font-semibold">
                  AED
                </span>

                <div className="text-xs text-muted-foreground">{p.type}</div>
                <div className="text-lg font-semibold">{p.provider}</div>

                <div className="mt-2 space-y-1 text-sm">
                  <div>Tenor: <span className="font-medium">{p.tenor} mo</span></div>
                  <div>APR: <span className="font-medium">{p.apr}%</span></div>
                  {p.fee ? <div>Fee: <span className="font-medium">AED {p.fee}</span></div> : null}
                </div>

                {/* Price block */}
                <div className="mt-3 flex items-baseline justify-between">
                  <div>
                    <div className="text-2xl font-extrabold leading-none">AED {p.monthly}/mo</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Total est: AED {p.total}</div>
                  </div>
                </div>

                {p.note ? <div className="mt-2 text-xs text-muted-foreground">{p.note}</div> : null}

                <button
                  className="mt-4 w-full rounded-lg border px-3 py-2 hover:bg-accent/20"
                  onClick={() =>
                    setSelected({
                      plan: { provider: p.provider, type: p.type as any, tenor: p.tenor, apr: (p as any).apr, fee: (p as any).fee, note: (p as any).note },
                      monthly: (p as any).monthly,
                      total: (p as any).total,
                      amount,
                      category,
                    })
                  }
                >
                  Preview checkout
                </button>
              </div>
            ))}
          </div>

          <p className="mt-4 text-xs text-muted-foreground">
            *Illustrative only. Actual terms vary by provider, amount, eligibility, and timing.
          </p>
        </div>
      </div>

      {selected ? (
        <PreviewModal
          selected={selected}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </section>
  );
}

// Simple inline modal for preview
function PreviewModal({
  selected,
  onClose,
}: {
  selected: Selected;
  onClose: () => void;
}) {
  if (!selected) return null;
  const { plan, monthly, total, amount, category } = selected;

  const query = new URLSearchParams({
    provider: plan.provider,
    type: plan.type,
    tenor: String(plan.tenor),
    apr: String(plan.apr),
    fee: String(plan.fee),
    amount: String(amount),
    monthly: String(monthly),
    total: String(total),
    category,
  }).toString();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl border bg-card p-6 shadow-xl">
        <h4 className="text-xl font-semibold">Checkout preview</h4>
        <p className="text-sm text-muted-foreground mt-1">
          Review your selection before you continue.
        </p>

        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-xl border p-3 bg-background">
            <div className="text-xs text-muted-foreground">Category</div>
            <div className="font-medium">{category}</div>
          </div>
          <div className="rounded-xl border p-3 bg-background">
            <div className="text-xs text-muted-foreground">Amount</div>
            <div className="font-medium">AED {amount}</div>
          </div>
          <div className="rounded-xl border p-3 bg-background">
            <div className="text-xs text-muted-foreground">Provider</div>
            <div className="font-medium">{plan.provider}</div>
          </div>
          <div className="rounded-xl border p-3 bg-background">
            <div className="text-xs text-muted-foreground">Plan</div>
            <div className="font-medium">
              {plan.type} • {plan.tenor} mo • APR {plan.apr}% {plan.fee ? `• Fee AED ${plan.fee}` : ""}
            </div>
          </div>
          <div className="rounded-xl border p-3 bg-background">
            <div className="text-xs text-muted-foreground">Estimated monthly</div>
            <div className="text-lg font-bold">AED {monthly}/mo</div>
          </div>
          <div className="rounded-xl border p-3 bg-background">
            <div className="text-xs text-muted-foreground">Estimated total</div>
            <div className="text-lg font-bold">AED {total}</div>
          </div>
        </div>

        <p className="mt-3 text-xs text-muted-foreground">
          You will complete KYC with the provider. Approval and final terms depend on eligibility and provider policies.
        </p>

        <div className="mt-5 flex items-center justify-end gap-3">
          <button className="rounded-lg border px-3 py-2" onClick={onClose}>
            Close
          </button>
          {/* Option A: keep modal only for MVP */}
          {/* Option B: route to a preview page with params */}
          <a
            href={`/checkout/preview?${query}`}
            className="rounded-lg bg-primary px-3 py-2 text-primary-foreground"
          >
            Continue
          </a>
        </div>
      </div>
    </div>
  );
}